import type { MartialSkillPath, MartialSkillPathCode } from "./path-registry.js"

export type MartialArtPath = MartialSkillPath

export type GuildWarMartialPathCode = MartialSkillPathCode

export type GuildWarAvailability = "saturday" | "sunday" | "both" | "permanent"
export type GuildWarWeeklyAvailability = "saturday" | "sunday" | "both"
export type GuildWarWeeklySignupState = "active" | "withdrawn"
export type GuildWarMemberStatus = "pending" | "active" | "inactive"
export type GuildMemberDiscordPresence = "present" | "departed" | "unknown"

export type GuildWarWeekState =
  | "draft"
  | "signup_open"
  | "planning"
  | "completed"

export type GuildWarPermanentApplicationState =
  | "pending"
  | "approved"
  | "rejected"
  | "withdrawn"
  | "expired"

export type GuildWarPermanentMembershipState = "active" | "paused" | "revoked"

export type GuildWarPermanentReviewDecision = "approved" | "rejected"

export type GuildWarAbsenceRequestedFor = "saturday" | "sunday" | "both"
export type GuildWarAbsenceRequestState =
  | "pending"
  | "approved"
  | "rejected"
  | "cancelled"
export type GuildWarAbsenceReviewDecision = "approved" | "rejected"

export type GuildWarMember = {
  userId: string

  discordUsername: string
  guildDisplayName: string

  primaryPathCode: MartialArtPath | null
  secondaryPathCode: MartialArtPath | null
  availability: GuildWarAvailability | null
  status: GuildWarMemberStatus
  deactivatedAt: Date | null
  deactivationNote: string | null
  discordPresence: GuildMemberDiscordPresence
  discordPresenceChangedAt: Date | null
  guildRoleFirstJoinedAt: Date | null

  createdAt: Date
  updatedAt: Date
}

export type GuildWarWeeklySignup = {
  id: string
  weekId: string
  memberId: string
  availability: GuildWarWeeklyAvailability
  state: GuildWarWeeklySignupState
  createdAt: Date
  updatedAt: Date
}

export type GuildWarPermanentApplication = {
  id: string
  memberId: string
  skillPathAtApply: MartialArtPath | null
  note: string | null
  state: GuildWarPermanentApplicationState
  expiresAt: Date
  reviewedBy: string | null
  reviewedAt: Date | null
  reviewNote: string | null
  createdAt: Date
  updatedAt: Date
}

export type GuildWarPermanentApplicationWithMember =
  GuildWarPermanentApplication & {
    guildDisplayName: string
  }

export type GuildWarPermanentMembership = {
  id: string
  memberId: string
  effectiveToWeekId: string | null
  state: GuildWarPermanentMembershipState
  createdAt: Date
  updatedAt: Date
}

export type GuildWarWeek = {
  id: string
  weekStartAt: Date
  weekEndAt: Date
  state: GuildWarWeekState
  createdAt: Date
  updatedAt: Date
}

export type GuildWarWeekAbsenceRequest = {
  id: string
  memberId: string
  startAt: Date
  endAt: Date
  reason: string | null
  state: GuildWarAbsenceRequestState
  reviewedByAdminId: string | null
  reviewedAt: Date | null
  createdAt: Date
  updatedAt: Date
}

export type GuildWarCandidateSource = "permanent" | "weekly" | "both"

export type SecondaryPathFilterValue = "all" | "none" | MartialArtPath

export type GuildWarCandidateAbsence = {
  id: string
  startAt: Date
  endAt: Date
  reason: string | null
  state: GuildWarAbsenceRequestState
}

export type GuildWarCandidatePoolMember = {
  memberId: string
  guildDisplayName: string
  permanentStatus: "active" | "pending" | "none"
  source: GuildWarCandidateSource
  availability: GuildWarWeeklyAvailability
  primaryPathCode: MartialArtPath | null
  secondaryPathCode: MartialArtPath | null
  absenceState: "none" | "pending" | "approved"
  absenceDays: GuildWarAbsenceRequestedFor | null
  overlappingAbsences: GuildWarCandidateAbsence[]
}

export type GuildWarPermanentMember = {
  membershipId: string
  memberId: string
  guildDisplayName: string
  primaryPathCode: MartialArtPath | null
  secondaryPathCode: MartialArtPath | null
  effectiveToWeekId: string | null
  state: GuildWarPermanentMembershipState
  createdAt: Date
  updatedAt: Date
}

export type GuildWarRosterTeamKey = "mobility" | "attack" | "defense"

export const GUILD_WAR_TEAM_DISPLAY: Record<
  GuildWarRosterTeamKey,
  { emoji: string; label: string; title: string }
> = {
  mobility: { emoji: "🐱", label: "Meowbility", title: "Meowbility" },
  attack: { emoji: "⚔️", label: "Nyanttack", title: "Nyanttack" },
  defense: { emoji: "🛡️", label: "Defence", title: "Defence Team" },
} as const

export type GuildWarRosterSquadKey = "alpha" | "beta"
export type GuildWarRosterRoleSlot = "commander" | "viceCommander"

export type GuildWarRosterSlotTarget =
  | {
      kind: "team-role"
      team: GuildWarRosterTeamKey
      role: GuildWarRosterRoleSlot
    }
  | {
      kind: "team-member"
      team: GuildWarRosterTeamKey
      index: number
    }
  | {
      kind: "subteam-member"
      team: GuildWarRosterTeamKey
      squad: GuildWarRosterSquadKey
      index: number
    }
  | {
      kind: "halftime"
      index: number
    }
  | {
      kind: "commander-overlay"
      index: number
    }

export type GuildWarRosterSubteam = {
  key: GuildWarRosterSquadKey
  members: Array<string | null>
}

export type GuildWarRosterMemberPathSnapshot = {
  primaryPathCode: GuildWarMartialPathCode | null
  secondaryPathCode: GuildWarMartialPathCode | null
}

export type GuildWarFlatTeamWithoutViceRoster = {
  commander: string | null
  viceCommander?: never
  members: Array<string | null>
}

export type GuildWarFlatTeamWithViceRoster = {
  commander: string | null
  viceCommander: string | null
  members: Array<string | null>
}

export type GuildWarSpecializedMobilityRoster = Omit<
  GuildWarFlatTeamWithViceRoster,
  "members"
> & {
  members: [string | null, string | null, string | null]
}

export type GuildWarFlatTeamRoster =
  | GuildWarFlatTeamWithoutViceRoster
  | GuildWarFlatTeamWithViceRoster

export type GuildWarSquadTeamRoster = {
  commander: string | null
  viceCommander: string | null
  subteams: [GuildWarRosterSubteam, GuildWarRosterSubteam]
}

export type GuildWarTeamRoster =
  | GuildWarFlatTeamRoster
  | GuildWarSquadTeamRoster

export type GuildWarRosterBoardMetadata = {
  memberPaths: Record<string, GuildWarRosterMemberPathSnapshot>
  memberDisplayNames: Record<string, string>
  commanders: [
    string | null,
    string | null,
    string | null,
    string | null,
    string | null,
  ]
  halftimePerformers: [string | null, string | null, string | null]
}

export type GuildWarThreeTeamRosterBoard = GuildWarRosterBoardMetadata & {
  mobility: GuildWarTeamRoster
  attack: GuildWarTeamRoster
  defense: GuildWarTeamRoster
}

export type GuildWarFlatRosterBoard = GuildWarThreeTeamRosterBoard & {
  mobility: GuildWarSpecializedMobilityRoster
  attack: GuildWarFlatTeamWithViceRoster
  defense: GuildWarFlatTeamWithViceRoster
}

export type GuildWarBalancedRosterBoard = GuildWarThreeTeamRosterBoard & {
  mobility: GuildWarSquadTeamRoster
  attack: GuildWarSquadTeamRoster
  defense: GuildWarSquadTeamRoster
}

export type GuildWarDualTeamRoster = Omit<
  GuildWarFlatTeamWithViceRoster,
  "members"
> & {
  members: [
    string | null,
    string | null,
    string | null,
    string | null,
    string | null,
    string | null,
    string | null,
    string | null,
    string | null,
    string | null,
    string | null,
    string | null,
    string | null,
  ]
}

export type GuildWarDualRosterBoard = GuildWarRosterBoardMetadata & {
  attack: GuildWarDualTeamRoster
  defense: GuildWarDualTeamRoster
  mobility?: never
}

export type GuildWarRosterBoard =
  | GuildWarThreeTeamRosterBoard
  | GuildWarDualRosterBoard

export type CandidatePoolSortBy =
  | "member"
  | "availability"
  | "primaryPath"
  | "secondaryPath"
  | "source"
  | "absenceState"

export type CandidatePoolSortOrder = "asc" | "desc"

export type ListCandidatePoolForWeekInput = {
  weekId: string
  day?: GuildWarAbsenceRequestedFor
  source?: GuildWarCandidateSource
  skillPaths?: MartialArtPath[]
  secondaryPath?: MartialArtPath | "none"
  absenceState?: GuildWarCandidatePoolMember["absenceState"]
  search?: string
  sortBy: CandidatePoolSortBy
  sortOrder: CandidatePoolSortOrder
  page: number
  pageSize: number
}

export type CandidatePoolPage = {
  items: GuildWarCandidatePoolMember[]
  total: number
  page: number
  pageSize: number
}

export type GuildWarRosterBoardRecord = {
  rosterDate: string
  sourceWeekId: string | null
  draft: GuildWarRosterBoard
  published: GuildWarRosterBoard | null
  version: number
  publishedVersion: number | null
  displayDate: string | null
  publishedDisplayDate: string | null
  publishedAt: Date | null
  publishedBy: string | null
  lastEditedAt: Date
  lastEditedBy: string | null
  createdAt: Date
  updatedAt: Date
}
